const API_URL = (import.meta.env.VITE_NEXT_EVENTS_API_URL ?? '').trim().replace(/\/+$/, '');

type RequestOptions = {
  token?: string;
  method?: 'GET' | 'POST';
  body?: Record<string, unknown>;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!API_URL) {
    throw new Error('Next Events API is not configured');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options.token) headers.Authorization = `Bearer ${options.token}`;

  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? (options.body ? 'POST' : 'GET'),
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload && typeof payload.error === 'string'
      ? payload.error
      : `Next Events API request failed with ${response.status}`;
    throw new Error(message);
  }
  return payload as T;
}

function hostParam(host: string) {
  return `host=${encodeURIComponent(host)}`;
}

function withHost(body: Record<string, unknown>, host: string) {
  return { host, ...body };
}

export const nextEventsApi = {
  health() {
    return request<{ ok: boolean; service: string }>('/health');
  },

  publicLanding(host: string) {
    return request<any>(`/public/landing?${hostParam(host)}`);
  },

  publicEvent(host: string, eventSlug: string, referralCode?: string) {
    const params = new URLSearchParams({ host });
    if (referralCode) params.set('ref', referralCode);
    return request<any>(`/public/events/${encodeURIComponent(eventSlug)}?${params.toString()}`);
  },

  referralTarget(host: string, code: string) {
    return request<any>(`/referrals/${encodeURIComponent(code)}?${hostParam(host)}`);
  },

  trackReferral(input: Record<string, unknown>) {
    return request<{ ok: boolean }>('/referrals/track', { body: input });
  },

  submitRsvp(input: Record<string, unknown>) {
    return request<any>('/rsvps', { body: input });
  },

  joinList(input: Record<string, unknown>) {
    return request<any>('/contacts/join', { body: input });
  },

  webhookUrl(provider: string, host: string) {
    return `${API_URL}/integrations/${encodeURIComponent(provider)}/webhook?${hostParam(host)}`;
  },

  seedDefaults(token: string) {
    return request<{ tenantId: string }>('/admin/seed', { method: 'POST', token, body: {} });
  },

  adminOverview(host: string, token: string) {
    return request<any>(`/admin/overview?${hostParam(host)}`, { token });
  },

  createOrUpdateEvent(host: string, token: string, input: Record<string, unknown>) {
    return request<string>('/admin/events', { token, body: withHost(input, host) });
  },

  createPromoter(host: string, token: string, input: Record<string, unknown>) {
    return request<string>('/admin/promoters', { token, body: withHost(input, host) });
  },

  updateRsvpStatus(host: string, token: string, input: Record<string, unknown>) {
    return request<string>('/admin/rsvps/status', { token, body: withHost(input, host) });
  },

  importGuestList(host: string, token: string, input: Record<string, unknown>) {
    return request<{ imported: number }>('/admin/import', { token, body: withHost(input, host) });
  },

  promoterDashboard(host: string, token: string) {
    return request<any>(`/promoter/dashboard?${hostParam(host)}`, { token });
  },

  doorList(host: string, eventSlug: string, token: string) {
    return request<any>(`/door/${encodeURIComponent(eventSlug)}?${hostParam(host)}`, { token });
  },

  checkIn(host: string, token: string, rsvpId: string) {
    return request<{ duplicate: boolean; checkedInAt?: number }>('/door/check-in', {
      token,
      body: { host, rsvpId },
    });
  },
};

export async function getRequiredAccessToken(getAccessToken: () => Promise<string | null | undefined>) {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to continue');
  return token;
}
